import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import RiskCardDetailsDialog from "./RiskCardDetailsDialog";
import AddRiskCardDialog from "./AddRiskCardDialog";

const RiskCard = ({ card, handleDragStart, handleDeleteCard, handleOpenEditDialog, isDragOver = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  
  // Simple check: if card status is "Done", disable editing and interactions
  const isDone = card?.cardStatus === 'Done';
  
  // console.log("RiskCard received card:", card);

  // Menu handlers
  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Edit
  const handleEdit = () => {
    handleOpenEditDialog(card);
    handleMenuClose();
  };

  // Delete
  const handleDelete = () => {
    //if (window.confirm(`Are you sure you want to delete ${card.riskSummary}?`)) {
      handleDeleteCard(card.id);
    
    handleMenuClose();
  };

  // Open details dialog
  const handleCardClick = () => setOpenDetailsDialog(true);
  const handleDetailsDialogClose = () => setOpenDetailsDialog(false);

  return (
    <>
      <Paper
        data-card-id={card.id}
        sx={{
          mb: 2,
          p: 0,
          borderRadius: 2,
          backgroundColor: "#E8EAE6",
          position: "relative",
          cursor: isDone ? "default" : "move",
          display: "flex",
          flexDirection: "column",
          transition: "box-shadow 0.3s, transform 0.3s",
          border: isDragOver ? "2px dashed #1976d2" : "none",
          "&:hover": {
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            transform: isDone ? "none" : "scale(1.02)",
          },
        }}
        draggable={!isDone}
        onDragStart={!isDone ? (event) => handleDragStart(event, card) : null}
      >
        {/* Top Bar */}
        <Box
          sx={{
            backgroundColor: "#1976d2",
            p: 1,
            display: "flex",
            alignItems: "center",
            height: "40px",
            width: "100%",
          }}
        >
          {/* Card ID */}
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              display: "block",
              lineHeight: "1",
              color: "white",
              width: "40%",
            }}
          >
            {card.cardId}
          </Typography>

          {/* Right Side: Priority + Menu */}
          <Box display="flex" flexDirection="row" width="60%" justifyContent="flex-end" alignItems="center">
              <Chip
               label={
               card?.templateData?.riskScore > 0
               ? card.templateData.riskScore
               : "N/A"
           }
          size="small"
          sx={{
            backgroundColor:
            card?.templateData?.riskScore >= 20
            ? "#f44336" // Very High Risk
            : card?.templateData?.riskScore >= 12
            ? "#ff9800" // High/Medium Risk
            : card?.templateData?.riskScore >= 5
            ? "#4caf50" // Low Risk
            : "#d3d3d3", // No risk
            color: "#fff",
            fontSize: "0.75rem",
            borderRadius: "25px",
            padding: "1px 6px",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
       }}
  />
            <IconButton 
              onClick={handleMenuClick} 
              disabled={isDone}
              sx={{ 
                color: "white", 
                padding: "0.5rem",
                opacity: isDone ? 0.5 : 1
              }}
            >
              <MoreVertIcon sx={{ fontSize: "1.2rem" }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleEdit} disabled={isDone}>Edit</MenuItem>
              <MenuItem onClick={handleDelete} disabled={isDone}>Delete</MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Middle Section: Summary */}
        <Box
          onClick={handleCardClick}
          sx={{
            backgroundColor: "#87CEEB",
            flexGrow: 1,
            p: 1,
            cursor: "pointer",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px", fontWeight: "bold" }}>
            {card.summary || "No summary provided"}
          </Typography>
        </Box>

        {/* Bottom Section */}
        <Box
          sx={{
            backgroundColor: "#d3d3d3",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "20px",
          }}
        >
          {/* Due Date */}
          <Box display="flex" alignItems="center">
            <TimerRoundedIcon sx={{ fontSize: "0.8rem", mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
              {card?.dueDate
        ? new Date(card.dueDate).toLocaleDateString("en-GB")
        : "No Due Date"}
            </Typography>
          </Box>

          {/* Owner Avatar */}
          <Tooltip title={card.assigneeName || card.assignee || card.actionOwner || "Unassigned"} arrow>
            <Avatar sx={{ bgcolor: "#1976d2", width: 17, height: 17, fontSize: "0.7rem" }}>
              {card?.assigneeName ? card.assigneeName.charAt(0).toUpperCase() : "?"}
            </Avatar>
          </Tooltip>
        </Box>
      </Paper>

      {/* Details Dialog */}
      <RiskCardDetailsDialog
        open={openDetailsDialog}
        onClose={handleDetailsDialogClose}
        card={card}
        onEdit={handleEdit}
      />
    </>
  );
};

export default RiskCard;
